import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  IconButton,
  Divider
} from '@mui/material';
import { Brightness4, Brightness7, DesktopWindows as DesktopWindowsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import { useColorMode } from '../context/ColorModeContext';

function Login() {
  const { t } = useTranslation();
  const { toggleColorMode, mode } = useColorMode();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Effacer l'erreur lors de la saisie
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(formData);

      // Stocker le token et les infos utilisateur
      localStorage.setItem('era_token', response.data.token);
      localStorage.setItem('era_user', JSON.stringify(response.data.user));

      // Rediriger vers la page d'origine (ex: invitation) ou le dashboard
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.error ||
        t('auth.loginError')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ alignSelf: 'flex-end', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={toggleColorMode} color="inherit">
            {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
          <LanguageSwitcher />
        </Box>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Logo sx={{ height: 80, maxHeight: 80 }} />
            </Box>
            <Typography variant="h6" color="text.secondary">
              {t('common.login')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('auth.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              margin="normal"
              autoComplete="email"
              autoFocus
            />

            <TextField
              fullWidth
              label={t('auth.password')}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              margin="normal"
              autoComplete="current-password"
            />

            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2" underline="hover">
                {t('auth.forgotPassword')}
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : t('common.login')}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2">
                {t('auth.no_account')}{' '}
                <Link component={RouterLink} to="/register" underline="hover">
                  {t('common.register')}
                </Link>
              </Typography>
            </Box>
          </form>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('auth.offlinePreference')}
            </Typography>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<DesktopWindowsIcon />}
              href="/ERA Project Setup 1.0.0.exe"
              download
              sx={{ textTransform: 'none' }}
            >
              {t('auth.downloadDesktop')}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default Login;
